import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { db } from '../firebase';
import { doc, onSnapshot, updateDoc, getDoc, deleteDoc, deleteField } from 'firebase/firestore';
import {
    Box, Typography, Paper, Grid, CircularProgress, Button, Divider,
    Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Link as MuiLink, Alert
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { formatCurrency, formatTarihSaat, formatTarih } from './PreInterviewFormUtils';
import { normalizeRandevuDurum, getRandevuDurumColor } from './AppointmentConstants';
import GeneralRandevuFormModal from './GeneralRandevuFormModal';
import NotFoundState from './NotFoundState';
import { useAuth } from '../context/AuthContext';

const SILEBILEN_ROLLER = ['Genel Müdür', 'Müdür'];

const RandevuDetay = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(true);
    // null: henüz kontrol edilmedi | true: iş emri duruyor | false: iş emri silinmiş
    const [workOrderVar, setWorkOrderVar] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        const docRef = doc(db, "appointments", id);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setAppointment({ ...docSnap.data(), id: docSnap.id });
            } else {
                setAppointment(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [id]);

    // Bağlı iş emri gerçekten duruyor mu? Silinmiş iş emrine işaret eden workOrderId
    // randevuyu kilitliyor ve ölü link üretiyordu.
    useEffect(() => {
        let iptal = false;
        const kontrolEt = async () => {
            if (!appointment?.workOrderId) { setWorkOrderVar(null); return; }
            try {
                const snap = await getDoc(doc(db, "workOrders", appointment.workOrderId));
                if (!iptal) setWorkOrderVar(snap.exists());
            } catch (e) {
                console.error("İş emri kontrolü başarısız:", e);
                if (!iptal) setWorkOrderVar(null);
            }
        };
        kontrolEt();
        return () => { iptal = true; };
    }, [appointment?.workOrderId]);

    const handleStatusUpdate = async (newStatus) => {
        try {
            await updateDoc(doc(db, "appointments", id), { durum: newStatus });
        } catch (error) {
            console.error("Durum güncellenirken hata:", error);
            alert("Durum güncellenirken bir hata oluştu.");
        }
    };

    // Silinmiş iş emrine giden bağı kopar: randevu yeniden kullanılabilir hale gelir.
    const handleBagiKopar = async () => {
        if (!window.confirm('Bu randevunun silinmiş iş emrine olan bağı kopacak ve randevu "Bekleniyor" durumuna dönecek. Devam edilsin mi?')) return;
        try {
            await updateDoc(doc(db, "appointments", id), {
                workOrderId: deleteField(),
                durum: 'Bekleniyor'
            });
            setWorkOrderVar(null);
        } catch (error) {
            console.error("Bağ koparılırken hata:", error);
            alert("İşlem sırasında bir hata oluştu.");
        }
    };

    const handleDelete = async () => {
        const onay =
            'Bu randevu kalıcı olarak silinecek.\n\n' +
            'Kaydı geçmişte tutmak istiyorsanız silmek yerine "İptal Et" seçeneğini kullanın.\n\n' +
            'BU İŞLEM GERİ ALINAMAZ. Devam edilsin mi?';
        if (!window.confirm(onay)) return;
        try {
            await deleteDoc(doc(db, "appointments", id));
            navigate('/randevular/liste', { replace: true });
        } catch (error) {
            console.error("Randevu silinirken hata:", error);
            alert("Silme işlemi sırasında bir hata oluştu.");
        }
    };

    const handleCreateWorkOrder = () => {
        let teslimTarihiStr = null;
        if (appointment.teslimTarihi) {
            const date = appointment.teslimTarihi.toDate
                ? appointment.teslimTarihi.toDate()
                : new Date(appointment.teslimTarihi);
            teslimTarihiStr = date.toISOString();
        }

        navigate('/is-emri-olustur', {
            state: {
                fromAppointment: true,
                appointmentId: appointment.id,
                preInterviewId: appointment.preInterviewId || null,
                musteriId: appointment.customerId,
                musteriAdSoyad: appointment.customerName,
                musteriTelefon: appointment.customerPhone,
                aracId: appointment.aracId,
                aracPlaka: appointment.aracPlaka,
                aracMarka: appointment.aracMarka,
                aracModel: appointment.aracModel,
                aracYil: appointment.aracYil,
                aracTuru: appointment.aracTuru,
                teslimTarihi: teslimTarihiStr,
                ilgilenilenHizmetler: appointment.ilgilenilenHizmetler,
                uygulamalar: appointment.uygulamalar,
                toplamTutar: appointment.toplamTutar,
                notlar: appointment.notes
            }
        });
    };

    if (loading) {
        return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
    }

    if (!appointment) {
        return (
            <NotFoundState
                title="Randevu bulunamadı"
                id={id}
                description="Bu randevu silinmiş olabilir. Kayıt duruyorsa Randevu Listesi'nden bulabilirsiniz."
                backTo="/randevular/liste"
                backLabel="Randevu Listesi"
            />
        );
    }

    const durum = normalizeRandevuDurum(appointment.durum);
    const isEmriBagiKopuk = Boolean(appointment.workOrderId) && workOrderVar === false;
    const showWorkOrderButton = (durum === 'Onaylandı' || durum === 'Bekleniyor') && (!appointment.workOrderId || isEmriBagiKopuk);
    const silebilir = SILEBILEN_ROLLER.includes(currentUser?.role);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EventIcon fontSize="large" color="primary" />
                    <Box>
                        <Typography variant="h4" component="h1">Randevu Detayı</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatTarihSaat(appointment.start)}
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                        label={durum}
                        color={getRandevuDurumColor(durum)}
                        size="medium"
                    />
                    <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setModalOpen(true)}>
                        Düzenle
                    </Button>
                    {silebilir && (
                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
                            Sil
                        </Button>
                    )}
                </Stack>
            </Box>

            {appointment.workOrderId && workOrderVar !== false && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Bu randevu için iş emri oluşturulmuş.
                    <Button
                        component={RouterLink}
                        to={`/is-emri/${appointment.workOrderId}`}
                        size="small"
                        sx={{ ml: 2 }}
                    >
                        İş Emrine Git
                    </Button>
                </Alert>
            )}

            {isEmriBagiKopuk && (
                <Alert
                    severity="warning"
                    sx={{ mb: 3 }}
                    action={
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button size="small" startIcon={<LinkOffIcon />} onClick={handleBagiKopar}>
                                Bağı Kopar
                            </Button>
                            <Button size="small" variant="contained" onClick={handleCreateWorkOrder}>
                                Yeni İş Emri Oluştur
                            </Button>
                        </Stack>
                    }
                >
                    Bu randevunun bağlı olduğu iş emri silinmiş. Randevu, bağ koparılana kadar kilitli kalır.
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Stack spacing={3}>
                        <Paper sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <PersonIcon color="primary" />
                                        <Typography variant="h6">Müşteri Bilgileri</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Stack spacing={1}>
                                        <Typography><b>Ad Soyad:</b> {appointment.customerName}</Typography>
                                        <Typography><b>Telefon:</b> {appointment.customerPhone}</Typography>
                                        {appointment.customerId && (
                                            <MuiLink
                                                component={RouterLink}
                                                to={`/musteri/${appointment.customerId}`}
                                                sx={{ textDecoration: 'none' }}
                                            >
                                                Müşteri Detayına Git →
                                            </MuiLink>
                                        )}
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <DirectionsCarIcon color="primary" />
                                        <Typography variant="h6">Araç Bilgileri</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Stack spacing={1}>
                                        <Typography><b>Plaka:</b> {appointment.aracPlaka || '-'}</Typography>
                                        <Typography><b>Marka/Model:</b> {appointment.aracMarka} {appointment.aracModel}</Typography>
                                        <Typography><b>Tip:</b> {appointment.aracTuru || '-'}</Typography>
                                        {appointment.aracId && (
                                            <MuiLink
                                                component={RouterLink}
                                                to={`/arac/${appointment.aracId}`}
                                                sx={{ textDecoration: 'none' }}
                                            >
                                                Araç Detayına Git →
                                            </MuiLink>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Hizmetler</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                {appointment.ilgilenilenHizmetler?.map((hizmet, index) => (
                                    <Chip key={index} label={hizmet} variant="outlined" />
                                )) || <Typography color="text.secondary">Hizmet seçilmemiş</Typography>}
                            </Box>

                            {appointment.uygulamalar && (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Uygulama</TableCell>
                                                <TableCell>Detay</TableCell>
                                                <TableCell align="right">Fiyat</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {appointment.uygulamalar.paketler?.map((p, i) => (
                                                <TableRow key={`paket-${i}`}>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Paket</TableCell>
                                                    <TableCell>{p.urunAdi || p.ad}</TableCell>
                                                    <TableCell align="right">{formatCurrency(p.fiyat)}</TableCell>
                                                </TableRow>
                                            ))}
                                            {['camFilmi', 'ppf', 'seramik'].map(kategori => {
                                                const label = { camFilmi: 'Cam Filmi', ppf: 'PPF', seramik: 'Seramik' }[kategori];
                                                return appointment.uygulamalar[kategori]?.map((p, i) => {
                                                    if (p.satisTipi === 'paket') {
                                                        return (
                                                            <React.Fragment key={`${kategori}-paket-${i}`}>
                                                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                                    <TableCell sx={{ fontWeight: 'bold' }}>{label} Paketi</TableCell>
                                                                    <TableCell sx={{ fontWeight: 'bold' }}>{p.paketAdi}</TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(p.paketFiyat || p.fiyat)}</TableCell>
                                                                </TableRow>
                                                                {(p.parcalar || []).filter(pr => pr.urunId).map((parca, pi) => (
                                                                    <TableRow key={`${kategori}-parca-${pi}`}>
                                                                        <TableCell sx={{ pl: 4 }}>↳</TableCell>
                                                                        <TableCell>{parca.ad}: {parca.urunAdi}</TableCell>
                                                                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatCurrency(parca.fiyat)}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </React.Fragment>
                                                        );
                                                    }
                                                    return (
                                                        <TableRow key={`${kategori}-${i}`}>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>{label}</TableCell>
                                                            <TableCell>{p.ad}: {p.urunAdi}</TableCell>
                                                            <TableCell align="right">{formatCurrency(p.fiyat)}</TableCell>
                                                        </TableRow>
                                                    );
                                                });
                                            })}
                                            {appointment.uygulamalar.diger?.map((h, i) => (
                                                <TableRow key={`diger-${i}`}>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>{h.tip || 'Diğer'}</TableCell>
                                                    <TableCell>{h.hizmetAdi || h.aciklama}</TableCell>
                                                    <TableCell align="right">{formatCurrency(h.fiyat)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Notlar ve Tarihler</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={1}>
                                <Typography><b>Randevu Zamanı:</b> {formatTarihSaat(appointment.start)} - {formatTarihSaat(appointment.end)}</Typography>
                                <Typography><b>Tahmini Teslim Alım:</b> {formatTarih(appointment.teslimTarihi)}</Typography>
                                <Typography><b>Notlar:</b> {appointment.notes || '-'}</Typography>
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Fiyat Bilgisi</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="h6">Toplam (KDV hariç):</Typography>
                                    <Typography variant="h5" color="primary.main" fontWeight="bold">
                                        {formatCurrency(appointment.toplamTutar)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Randevu Durumu</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={1}>
                                {durum === 'Bekleniyor' && (
                                    <>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={<CheckCircleIcon />}
                                            onClick={() => handleStatusUpdate('Onaylandı')}
                                            fullWidth
                                        >
                                            Onayla
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<CancelIcon />}
                                            onClick={() => handleStatusUpdate('İptal')}
                                            fullWidth
                                        >
                                            İptal Et
                                        </Button>
                                    </>
                                )}

                                {durum === 'Onaylandı' && !appointment.workOrderId && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<CancelIcon />}
                                        onClick={() => handleStatusUpdate('İptal')}
                                        fullWidth
                                    >
                                        İptal Et
                                    </Button>
                                )}

                                {/* İptal geri alma: iptal edilen randevu takvimde gizlenir,
                                    yanlışlıkla iptal edilmişse buradan geri alınır. */}
                                {durum === 'İptal' && (
                                    <>
                                        <Alert severity="warning">
                                            Bu randevu iptal edilmiş; takvimde görünmez.
                                        </Alert>
                                        <Button
                                            variant="contained"
                                            startIcon={<RestoreIcon />}
                                            onClick={() => handleStatusUpdate('Bekleniyor')}
                                            fullWidth
                                        >
                                            İptali Geri Al
                                        </Button>
                                    </>
                                )}

                                {/* İş Emri Oluştur - iş emri yoksa ya da bağlı iş emri silinmişse */}
                                {showWorkOrderButton && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<PlaylistAddCheckIcon />}
                                        onClick={handleCreateWorkOrder}
                                        fullWidth
                                        sx={{ mt: 2 }}
                                    >
                                        Araç Teslim Alındı - İş Emri Oluştur
                                    </Button>
                                )}

                                {/* İş Emri Varsa - Oraya Git */}
                                {appointment.workOrderId && workOrderVar !== false && durum !== 'Tamamlandı' && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<PlaylistAddCheckIcon />}
                                        component={RouterLink}
                                        to={`/is-emri/${appointment.workOrderId}`}
                                        fullWidth
                                    >
                                        İş Emrini Görüntüle
                                    </Button>
                                )}

                                {/* Onaylı ama iş emri yok - Bilgilendirme */}
                                {durum === 'Onaylandı' && !appointment.workOrderId && (
                                    <Alert severity="info" sx={{ mt: 1 }}>
                                        Müşteri aracı getirdiğinde "İş Emri Oluştur" butonuna tıklayın.
                                    </Alert>
                                )}

                                {/* Tamamlandı durumu - sadece bilgi */}
                                {durum === 'Tamamlandı' && (
                                    <Alert severity="success" sx={{ mt: 1 }}>
                                        Bu randevu tamamlanmış.
                                    </Alert>
                                )}
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>

            <GeneralRandevuFormModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                initialData={appointment}
                userRole={currentUser?.role}
            />
        </Box>
    );
};

export default RandevuDetay;
